import React, { useState, useRef, useEffect } from 'react';

interface WorkInProgressBannerProps {
  initialOffset?: number;
  isEditing?: boolean;
  onOffsetChange?: (offset: number) => void;
}

export const WorkInProgressBanner: React.FC<WorkInProgressBannerProps> = ({
  initialOffset = 0,
  isEditing = false,
  onOffsetChange
}) => {
  const [diagonalOffset, setDiagonalOffset] = useState(initialOffset);
  const [isDragging, setIsDragging] = useState(false);
  const [bannerWidth, setBannerWidth] = useState(600);
  const containerRef = useRef<HTMLDivElement>(null);
  const dragStartRef = useRef<{ mouseX: number; mouseY: number; startOffset: number }>({
    mouseX: 0,
    mouseY: 0,
    startOffset: 0
  });

  useEffect(() => {
    const updateBannerWidth = () => {
      if (containerRef.current) {
        const { width, height } = containerRef.current.getBoundingClientRect();
        const diagonal = Math.sqrt(width * width + height * height);
        setBannerWidth(diagonal * 2);
      }
    };

    updateBannerWidth();

    const resizeObserver = new ResizeObserver(updateBannerWidth);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    window.addEventListener('resize', updateBannerWidth);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateBannerWidth);
    };
  }, []);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!isEditing) return;
    e.stopPropagation();
    setIsDragging(true);
    dragStartRef.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      startOffset: diagonalOffset
    };
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging || !containerRef.current) return;

    const deltaX = e.clientX - dragStartRef.current.mouseX;
    const deltaY = e.clientY - dragStartRef.current.mouseY;

    const diagonalDelta = (deltaX + deltaY) / Math.sqrt(2);
    const newOffset = dragStartRef.current.startOffset + diagonalDelta;

    const { width, height } = containerRef.current.getBoundingClientRect();
    const minOffset = -baseTop * 1.1;
    const maxOffset = (width + height - baseTop) * 0.6;

    const clampedOffset = Math.max(minOffset, Math.min(maxOffset, newOffset));
    setDiagonalOffset(clampedOffset);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    if (onOffsetChange) {
      onOffsetChange(diagonalOffset);
    }
  };

  React.useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);

      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, diagonalOffset]);

  const baseTop = 70;
  const baseLeft = -200;
  const originalWidth = 600;
  const widthDiff = bannerWidth - originalWidth;
  const centeredLeft = baseLeft - (widthDiff / 2);

  const diagonalToXY = (offset: number) => {
    const cos45 = Math.cos(Math.PI / 4);
    const sin45 = Math.sin(Math.PI / 4);
    return {
      x: offset * cos45,
      y: offset * sin45
    };
  };

  const position = diagonalToXY(diagonalOffset);

  const baseText = 'WORK IN PROGRESS';
  const separator = ' · ';
  const textUnit = baseText + separator;
  const avgCharWidth = 9.5;
  const repetitions = Math.ceil(bannerWidth / (textUnit.length * avgCharWidth)) + 2;
  const repeatedText = Array(repetitions).fill(textUnit).join('');

  return (
    <div
      ref={containerRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        pointerEvents: isDragging ? 'auto' : 'none',
        zIndex: 1000
      }}
    >
      <div
        onMouseDown={handleMouseDown}
        style={{
          position: 'absolute',
          top: `${baseTop + position.y}px`,
          left: `${centeredLeft + position.x}px`,
          width: `${bannerWidth}px`,
          backgroundColor: '#F9D75B',
          color: '#000000',
          textAlign: 'center',
          fontSize: '15.6px',
          fontWeight: '400',
          letterSpacing: '0.5px',
          padding: '7.2px 0',
          transform: 'rotate(-45deg)',
          transformOrigin: 'center center',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
          fontFamily: 'Impact, "Arial Black", sans-serif',
          cursor: isEditing ? (isDragging ? 'grabbing' : 'grab') : 'default',
          pointerEvents: 'auto',
          userSelect: 'none',
          whiteSpace: 'nowrap',
          overflow: 'hidden'
        }}
      >
        {repeatedText}
      </div>
    </div>
  );
};
